"use client";

import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import classnames from "classnames";

import styles from "./SnapCarousel.module.scss";

export type SnapCarouselHandle = {
  prev: () => void;
  next: () => void;
  goTo: (index: number) => void;
};

type SnapCarouselProps = {
  children: React.ReactNode;
  className?: string;
  scrollerClassName?: string;
  slideClassName?: string;
  dots?: boolean;
  ariaLabel?: string;
};

/**
 * CSS scroll-snap replacement for the antd Carousel (react-slick).
 * Slides are plain server-rendered flex children, so there is no
 * hydration-time re-layout (the antd carousel injected its styles only at
 * hydration, causing CLS), no cloned-slide DOM duplication, and no
 * client JS on the critical rendering path. Swiping is native scrolling;
 * arrows/dots drive scrollTo. Arrow navigation wraps around like slick's
 * `infinite` mode.
 */
const SnapCarousel = forwardRef<SnapCarouselHandle, SnapCarouselProps>(
  function SnapCarousel(props, ref) {
    const { children, className, scrollerClassName, slideClassName, dots, ariaLabel } = props;

    const scrollerRef = useRef<HTMLDivElement>(null);
    const [activeIndex, setActiveIndex] = useState(0);
    const slides = React.Children.toArray(children);

    const getMetrics = useCallback(() => {
      const scroller = scrollerRef.current;
      const firstSlide = scroller?.firstElementChild as HTMLElement | null;
      const step = firstSlide?.offsetWidth || scroller?.clientWidth || 1;
      const visibleCount = Math.max(1, Math.round((scroller?.clientWidth || step) / step));
      return { scroller, step, visibleCount };
    }, []);

    const goTo = useCallback((index: number) => {
      const { scroller, step, visibleCount } = getMetrics();
      if (!scroller) return;
      const maxIndex = Math.max(0, slides.length - visibleCount);
      const target = Math.min(Math.max(index, 0), maxIndex);
      scroller.scrollTo({ left: target * step, behavior: "smooth" });
    }, [getMetrics, slides.length]);

    const move = useCallback((delta: number) => {
      const { scroller, step, visibleCount } = getMetrics();
      if (!scroller) return;
      const maxIndex = Math.max(0, slides.length - visibleCount);
      const current = Math.round(scroller.scrollLeft / step);
      let target = current + delta;
      // Wrap around at the edges, mirroring slick's `infinite` arrows.
      if (target > maxIndex) target = 0;
      if (target < 0) target = maxIndex;
      scroller.scrollTo({ left: target * step, behavior: "smooth" });
    }, [getMetrics, slides.length]);

    useImperativeHandle(ref, () => ({
      prev: () => move(-1),
      next: () => move(1),
      goTo,
    }), [move, goTo]);

    // Track the active slide and hide off-screen slides from AT/tab order,
    // as slick did. Attributes are only set post-hydration and have no
    // layout effect, so this cannot shift anything.
    useEffect(() => {
      const scroller = scrollerRef.current;
      if (!scroller) return;

      let frame = 0;
      const sync = () => {
        frame = 0;
        const { step, visibleCount } = getMetrics();
        const index = Math.round(scroller.scrollLeft / step);
        setActiveIndex(index);
        Array.from(scroller.children).forEach((child, i) => {
          const hidden = i < index || i >= index + visibleCount;
          child.setAttribute("aria-hidden", hidden ? "true" : "false");
          (child as HTMLElement).toggleAttribute("inert", hidden);
        });
      };

      const onScroll = () => {
        if (!frame) frame = requestAnimationFrame(sync);
      };

      sync();
      scroller.addEventListener("scroll", onScroll, { passive: true });
      window.addEventListener("resize", onScroll);
      return () => {
        if (frame) cancelAnimationFrame(frame);
        scroller.removeEventListener("scroll", onScroll);
        window.removeEventListener("resize", onScroll);
      };
    }, [getMetrics, slides.length]);

    return (
      <div
        className={classnames(styles.carousel, className)}
        role="region"
        aria-roledescription="carousel"
        aria-label={ariaLabel}
      >
        <div ref={scrollerRef} className={classnames(styles.scroller, scrollerClassName)}>
          {slides.map((child, index) => (
            <div key={index} className={classnames(styles.slide, slideClassName)}>
              {child}
            </div>
          ))}
        </div>

        {dots && slides.length > 1 && (
          <ul className={styles.dots}>
            {slides.map((_, index) => (
              <li key={index} className={classnames(index === activeIndex && styles.dotActive)}>
                <button
                  type="button"
                  aria-label={`${index + 1} / ${slides.length}`}
                  onClick={() => goTo(index)}
                />
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  }
);

export default SnapCarousel;
